<script setup lang="ts">
/**
 * /ai-cover-letter — retargeted from the product name to the question people
 * search: "how to write a cover letter", "what is a cover letter", "how to
 * format a cover letter", "who do you address a cover letter to".
 *
 * The generator did not move, it just stopped being the first thing on the
 * page. Order is answer, worked example, then the tool.
 *
 * /templates carries a short four-paragraph skeleton and links here for the
 * long version; this page must stay the deeper of the two.
 */
const { t } = useI18n()
const { faqPageNode } = useJsonLd()

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/motivacni-dopis', de: '/anschreiben', pl: '/list-motywacyjny' } })

/** Answered in prose on the page, and mirrored into FAQPage structured data. */
const FAQ_IDS = ['length', 'twoPages', 'difference', 'every', 'noExperience', 'ai', 'pdf'] as const

const faqItems = computed(() =>
  FAQ_IDS.map(id => ({
    id,
    question: t(`coverLetterGuide.faq.${id}.q`),
    answer: t(`coverLetterGuide.faq.${id}.a`),
  })),
)

const extraNodes = computed(() => [faqPageNode('/ai-cover-letter', faqItems.value)])
</script>

<template>
  <MarketingFeaturePage
    seo-key="aiCoverLetter"
    path="/ai-cover-letter"
    :breadcrumb-title="t('coverLetterGuide.heading')"
    :extra-nodes="extraNodes"
  >
    <MarketingCoverLetterGuide />

    <!-- English only: UK public-sector jobs ask for a statement, not a cover letter -->
    <MarketingUkToolsNote
      text="The UK Civil Service and the NHS rarely ask for a cover letter. They score a personal statement, behaviour statements or supporting information against the advert's criteria instead, within a word or character limit."
    />

    <!-- The generator, start to PDF -->
    <MarketingFeatureVideo video="letter" />

    <LazyHomepageAiCoverLetterSection />

    <MarketingCoverLetterPdfBand />

    <MarketingFaqSection
      :title="t('coverLetterGuide.faq.title')"
      :items="faqItems"
      name="cover-letter-faq"
    />
  </MarketingFeaturePage>
</template>
